import { EventType } from './common';
import { Sentry } from '$lib/services/sentry';
import {
	validateAppLoadEvent,
	validateAuxiliarySubscriptionEvent,
	validateClickEvent,
	validatePageLoadEvent,
	validateSubscribeEvent,
	validateUpgradeEvent,
} from './validation';
/**
 * @type {import('./types').EventLogger.Logger}
 */
export default class EventLogger {
	/**
	 * @param {string} name name of logger
	 */
	constructor(name) {
		this.name = name;

		this.subscriptionMap = {
			[EventType.AppLoad]: [],
			[EventType.Auxiliary]: [],
			[EventType.Click]: [],
			[EventType.LogIn]: [],
			[EventType.Ltv]: [],
			[EventType.OptOut]: [],
			[EventType.PageLoadNmhp]: [],
			[EventType.PageLoadSub]: [],
			[EventType.Purchase]: [],
			[EventType.Reactivate]: [],
			[EventType.PageLoad]: [],
			[EventType.Subscribe]: [],
			[EventType.Upgrade]: [],
		};

		this.validationMap = {
			[EventType.AppLoad]: validateAppLoadEvent,
			[EventType.Auxiliary]: validateAuxiliarySubscriptionEvent,
			[EventType.Click]: validateClickEvent,
			[EventType.PageLoad]: validatePageLoadEvent,
			[EventType.Subscribe]: validateSubscribeEvent,
			[EventType.Upgrade]: validateUpgradeEvent,
		};
	}

	/**
	 * Adds a subscriber (Transport) to the list for the specified event type.
	 * @param {EventType} eventType - The type of event.
	 * @param {import('./types').Transports.Transport} transport - The subscriber transport.
	 * @returns {boolean} - True if the subscriber is added successfully.
	 */
	addSubscriber(eventType, transport) {
		if (!this.subscriptionMap[eventType]) {
			this.subscriptionMap[eventType] = [];
		}
		this.subscriptionMap[eventType].push(transport);
		return true;
	}

	/**
	 * Removes a subscriber (Transport) from the list for the specified event type.
	 * @param {EventType} eventType - The type of event.
	 * @param {import('./types').Transports.Transport} transport - The subscriber transport.
	 * @returns {boolean} - True if the subscriber is removed successfully.
	 */
	removeSubscriber(eventType, transport) {
		if (!this.subscriptionMap[eventType]) return false;

		const index = this.subscriptionMap[eventType].indexOf(transport);
		if (index !== -1) {
			this.subscriptionMap[eventType].splice(index, 1);
			return true;
		}
		return false;
	}

	/**
	 * Logs an event by sending it to all subscribers of the event type.
	 * @param {import("./types").Events.Event<EventType, object>} event - event to send
	 * @param {object} [options] - Options to control execution order.
	 * @param {'parallel' | 'series'} options.transportExecutionOrder - Controls if events are transmitted in parallel or series.
	 * @returns {Promise<object>} - Resolves to an object with the transport names as keys and consume return values as values.
	 */
	async log(event, options = { transportExecutionOrder: 'series' }) {
		const subscribers = this.subscriptionMap[event.type] || [];
		/** @type {{ [key: string]: string }}  */
		const results = {};

		if (subscribers.length === 0) {
			return results;
		}

		let validatedEvent = event;
		if (this.validationMap[event.type]) validatedEvent = this.validationMap[event.type](event);

		if (options.transportExecutionOrder === 'parallel') {
			const transportResults = await Promise.all(
				subscribers.map(
					async (subscriber) => await this.handleTransport(subscriber, validatedEvent)
				)
			);
			transportResults.forEach(({ name, result }) => {
				results[name] = result;
			});
		} else {
			for (const subscriber of subscribers) {
				const { name, result } = await this.handleTransport(subscriber, validatedEvent);
				results[name] = result;
			}
		}

		return results;
	}

	/**
	 * A pure function to handle a transport's consume method.
	 * @param {import('./types').Transports.Transport} transport - The transport.
	 * @param {import("./types").Events.Event<EventType, object>} event - event to send
	 * @returns {Promise<{ name: string, result: any }>} - An object containing the transport name and the result of consume.
	 */
	async handleTransport(transport, event) {
		try {
			const result = await transport.consume(event);
			return { name: transport.name, result };
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: `Error: ${transport.name} Transport`,
				},
			});
			return { name: transport.name, result: `error ${error?.toString()} ` };
		}
	}
}
